import { revisionedRead } from './readCache';
import { markRequest } from '../lib/actionFailureSignals';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { editedFieldsToDbColumns } from '../domain/orderLines/editedFields';
import type { LineAction } from '../pages/event/order/itemTypes';
import { normaliseLineAction } from '../pages/event/order/itemTypes';
import type { KitPricingMode } from '../pages/event/order/itemUtils';
import { normaliseKitPricingMode } from '../pages/event/order/itemUtils';
import { getSupabaseClient } from './supabase';
import { AbortError, TimeoutError, isAbortLikeError, withTimeoutAbortable } from './withTimeoutAbortable';
import {
  applyLatestSmartPricingToEvent,
  isSmartPricingSchemaUnavailable,
  refreshSmartPricingDiscountException,
} from './smartPricing';
import {
  createOrderWorkspaceCorrelationId,
  logOrderWorkspaceEvent,
  type OrderWorkspaceRequestMeta,
} from '../lib/orderWorkspaceLogger';
import { registerSessionCleanup } from '../auth/sessionCleanup';

// Extended item type for custom column support
export type EventLineItemData = {
  item_id?: string | null;
  item_type_id?: number | string | null;
  /** Authoritative kit-parent stock identity from the workspace snapshot. */
  kit_parent_is_physical?: boolean | null;
  item_code?: string | null;
  name_internal?: string | null;
  category_id?: string | null;
  default_charge_days?: number | null;
  brand?: string | null;
  mpn?: string | null;
  model_name?: string | null;
  model_number?: string | null;
  manufacturer_name?: string | null;
  replacement_cost?: number | null;
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  can_rent?: boolean | null;
  can_sell?: boolean | null;
  is_bulk?: boolean | null;
  has_unlimited_availability?: boolean | null;
};

// Extended product type for custom column support
export type EventLineProductData = {
  product_id?: string | null;
  product_code?: string | null;
  name_public?: string | null;
  short_description?: string | null;
  long_description?: string | null;
  category_id?: string | null;
  default_charge_days?: number | null;
  default_item_id?: string | null;
  uom?: string | null;
  status?: string | null;
  visible_to_client?: boolean | null;
};

// Extended room access type
export type EventLineRoomAccessData = {
  id?: string | null;
  event_room_id?: string | null;
  room_name?: string | null;
  room_type?: string | null;
  access_date?: string | null;
  access_start_time?: string | null;
  access_end_time?: string | null;
  access_notes?: string | null;
};

// Section type (global sections table; used by event_lines)
export type EventLineSectionData = {
  section_id?: string | null;
  code?: string | null;
  name?: string | null;
  description?: string | null;
  position?: number | null;
  sort_order?: number | null;
};

export type EventLineRecord = {
  event_line_id: string;
  event_id: string;
  action?: string | null;
  kind?: string | null; // 'section', 'section_subtotal', 'labor', or null for regular items
  sku?: string | null;
  item_code?: string | null;
  product_code?: string | null;
  product_id?: string | null;
  item_id?: string | null;
  section_id?: string | null;
  description?: string | null;
  note?: string | null;
  qty?: number | null;
  charge_days?: number | null;
  unit_price?: number | null;
  discount?: number | null;
  r2_allow_discount?: boolean | null;
  r2_discount_amount?: number | null;
  r2_pre_discount_amount?: number | null;
  r2_net_amount?: number | null;
  r2_net_discount_pct?: number | null;
  smart_unit_price?: number | null;
  smart_max_discount_pct?: number | null;
  smart_pricing_snapshot_id?: string | null;
  smart_pricing_reason?: string | null;
  smart_pricing_signals?: Record<string, unknown> | null;
  smart_discount_exception_status?: string | null;
  line_number?: number | null;
  not_discountable?: boolean | null;
  non_printable?: boolean | null;
  section_show_subtotal?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  event_room_access_id?: string | null;
  room_key?: string | null;
  usage_room_date?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  // Extended JOIN data for custom columns
  items?: EventLineItemData | null;
  products?: EventLineProductData | null;
  event_room_access?: EventLineRoomAccessData | null;
  /** Section (global sections table); event_lines use sections, not order_sections */
  sections?: EventLineSectionData | null;
  fill_date?: string | null;
  fill_time?: string | null;
  return_date?: string | null;
  return_time?: string | null;
  kit_anchor?: boolean | null;
  kit_component?: boolean | null;
  kit_instance_id?: string | null;
  kit_item_id?: string | null;
  kit_component_id?: string | null;
  kit_parent_event_line_id?: string | null;
  kit_label?: string | null;
  kit_pricing_mode?: KitPricingMode | null;
  r2_contract_line_iid?: string[] | null;
  /** True when charge_days is an authoritative per-date source allocation. */
  source_charge_days_authoritative?: boolean | null;
  event_line_custom_fields?: Array<{
    column_key: string;
    field_value: string | null;
  }> | null;
  /** Server-assigned rollup group ID for "All" view grouping */
  rollup_group_id?: string | null;
  /** Quick turnaround override — item treated as available despite reservation conflicts */
  quick_turn?: boolean | null;
  /** PostgreSQL xmin system column as row version for optimistic concurrency */
  row_version?: string | null;
};

export type FetchEventLinesResult = {
  lines: EventLineRecord[];
  error: FetchEventLinesError | null;
};

export type FetchEventLinesOptions = {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
  signal?: AbortSignal | null;
  dateYmd?: string | null;
  timeoutMs?: number;
  correlationId?: string;
  retry?: FetchEventLinesRetryOptions;
};

export type FetchEventLinesRetryOptions = {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  jitterRatio?: number;
};

type EventItemsWorkspaceSnapshotPayload = {
  lines: unknown[];
  items?: unknown[];
  products?: unknown[];
  room_access?: unknown[];
  sections?: unknown[];
  custom_fields?: unknown[];
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeWorkspaceSnapshotPayload = (
  value: unknown,
): EventItemsWorkspaceSnapshotPayload | null => {
  const payload = Array.isArray(value) && value.length === 1 ? value[0] : value;
  if (!isPlainRecord(payload) || !Array.isArray(payload.lines)) return null;
  return payload as unknown as EventItemsWorkspaceSnapshotPayload;
};

const indexWorkspaceRecords = (
  rows: unknown[] | undefined,
  key: string,
): Map<string, Record<string, unknown>> => {
  const index = new Map<string, Record<string, unknown>>();
  for (const row of rows ?? []) {
    if (!isPlainRecord(row)) continue;
    const id = row[key];
    if (typeof id === 'string' && id) index.set(id, row);
  }
  return index;
};

/**
 * Rehydrates the normalized workspace RPC payload into the same relationship
 * shape returned by the legacy nested PostgREST event_lines select.
 */
export const hydrateEventItemsWorkspaceSnapshot = (
  value: unknown,
): EventLineRecord[] | null => {
  const payload = normalizeWorkspaceSnapshotPayload(value);
  if (!payload) return null;

  const itemsById = indexWorkspaceRecords(payload.items, 'item_id');
  const productsById = indexWorkspaceRecords(payload.products, 'product_id');
  const roomAccessById = indexWorkspaceRecords(payload.room_access, 'id');
  const sectionsById = indexWorkspaceRecords(payload.sections, 'section_id');
  const customFieldsByLineId = new Map<string, Array<{ column_key: string; field_value: string | null }>>();

  for (const value of payload.custom_fields ?? []) {
    if (!isPlainRecord(value)) continue;
    const eventLineId = value.event_line_id;
    const columnKey = value.column_key;
    if (typeof eventLineId !== 'string' || !eventLineId || typeof columnKey !== 'string') continue;
    const fields = customFieldsByLineId.get(eventLineId) ?? [];
    fields.push({
      column_key: columnKey,
      field_value: value.field_value == null ? null : String(value.field_value),
    });
    customFieldsByLineId.set(eventLineId, fields);
  }

  const lines: EventLineRecord[] = [];
  for (const value of payload.lines) {
    if (!isPlainRecord(value)) continue;
    const eventLineId = value.event_line_id;
    const eventId = value.event_id;
    if (typeof eventLineId !== 'string' || !eventLineId || typeof eventId !== 'string' || !eventId) continue;

    const itemId = typeof value.item_id === 'string' ? value.item_id : null;
    const productId = typeof value.product_id === 'string' ? value.product_id : null;
    const roomAccessId = typeof value.event_room_access_id === 'string' ? value.event_room_access_id : null;
    const sectionId = typeof value.section_id === 'string' ? value.section_id : null;

    lines.push({
      ...value,
      event_line_id: eventLineId,
      event_id: eventId,
      items: itemId ? (itemsById.get(itemId) as EventLineItemData | undefined) ?? null : null,
      products: productId ? (productsById.get(productId) as EventLineProductData | undefined) ?? null : null,
      event_room_access: roomAccessId
        ? (roomAccessById.get(roomAccessId) as EventLineRoomAccessData | undefined) ?? null
        : null,
      sections: sectionId
        ? (sectionsById.get(sectionId) as EventLineSectionData | undefined) ?? null
        : null,
      event_line_custom_fields: customFieldsByLineId.get(eventLineId) ?? [],
    } as EventLineRecord);
  }

  return lines;
};

const isMissingEventItemsWorkspaceSnapshotRpc = (error: unknown): boolean => {
  if (!isPlainRecord(error)) return false;
  const code = String(error.code ?? '');
  const message = String(error.message ?? '').toLowerCase();
  return code === '42883'
    || code === 'PGRST202'
    || (
      message.includes('event_items_workspace_snapshot')
      && (
        message.includes('does not exist')
        || message.includes('could not find')
        || message.includes('schema cache')
      )
    );
};

/** Step 1 instrumentation: write payload capture for overwrite-bug detection */
export type WritePayloadInstrumentation = {
  payloadColumns: string[];
  eventLineId: string | null;
  clientReadVersion: string | null;
  payload: Record<string, unknown>;
  isUpdate: boolean;
  /** Step 3: If true on update, this write is changing line_number (possible hidden reorder). Non-reorder edits must not send line_number. */
  payloadIncludesLineNumber: boolean;
};

export type EventLineUpsertInput = {
  supabase?: SupabaseClient;
  eventId: string;
  eventLineId?: string | null;
  updatedAt?: string | null;
  eventRoomAccessId?: string | null;
  roomKey?: string | null;
  usageRoomDate?: string | null;
  action?: LineAction | null;
  kind?: string | null | undefined; // 'section', 'section_subtotal', 'labor', or null
  productId?: string | null | undefined;
  itemId?: string | null | undefined;
  sectionId?: string | null | undefined;
  description?: string | null | undefined;
  note?: string | null | undefined;
  qty?: number | null;
  chargeDays?: number | null | undefined;
  unitPrice?: number | null | undefined;
  discountPct?: number | null | undefined;
  notDiscountable?: boolean | null | undefined;
  nonPrintable?: boolean | null | undefined;
  sectionShowSubtotal?: boolean | null | undefined;
  lineNumber?: number | null | undefined;
  fill_date?: string | null;
  fill_time?: string | null;
  ship_date?: string | null;
  ship_time?: string | null;
  return_date?: string | null;
  return_time?: string | null;
  kitAnchor?: boolean | null;
  kitComponent?: boolean | null;
  kitInstanceId?: string | null;
  kitItemId?: string | null;
  kitComponentId?: string | null;
  kitParentEventLineId?: string | null;
  kitLabel?: string | null;
  kitPricingMode?: KitPricingMode | string | null;
  r2ContractLineIid?: string[] | null;
  /** Optional: instrument write payloads (columns, event_line_id, client read version). If payload includes columns the user did not edit, that is the overwrite bug. */
  _instrumentWrite?: (data: WritePayloadInstrumentation) => void;
  /** Optional (dev): after UPDATE, log the row returned by the DB to verify write round-trip (charge_days, discount, etc.). */
  _instrumentWriteResult?: (row: {
    event_line_id: string;
    action?: string | null;
    charge_days?: number | null;
    discount?: number | null;
    line_number?: number | null;
    updated_at?: string | null;
    xmin?: unknown;
  }) => void;
  /**
   * Optional: for dev-only strict payload assertion. Must be known keys (see domain/orderLines/editedFields).
   * Each entry is mapped to a DB column in one place; unknown keys throw UNKNOWN_EDITED_FIELD.
   */
  _editedFields?: string[];
};

export type EventLineUpsertResult = {
  eventLineId: string | null;
  updatedAt: string | null;
  error: PostgrestError | null;
};

export type EventLineSequenceEntry = {
  eventLineId: string;
  lineNumber: number;
};

export type EventLineDiscountMode = 'apply' | 'clear';

export type EventLineDiscountContext = {
  ready: boolean;
  discountableLineIds: string[];
  totalPre: number;
  weightedPercent: number | null;
  uniformPercent: number | null;
  discountableCount: number;
  lineCount: number;
  smartCapLineCount: number;
  smartMaxDiscountPct: number | null;
  smartOverCapLineCount: number;
  smartMaxOverCapPct: number;
};

export type FetchEventLinesError = PostgrestError | TimeoutError | AbortError;

type InflightFetchEntry = {
  promise: Promise<FetchEventLinesResult>;
  startedAt: number;
  correlationId: string;
  attempt: number;
  dedupeLogged?: boolean;
  dedupeCorrelationIds?: Set<string>;
};

const DEFAULT_EVENT_LINES_TIMEOUT_MS = 30000;
const DEFAULT_EVENT_LINES_MAX_RETRIES = 2;
const DEFAULT_EVENT_LINES_RETRY_BASE_MS = 500;
const DEFAULT_EVENT_LINES_RETRY_MAX_MS = 4000;
const DEFAULT_EVENT_LINES_RETRY_JITTER_RATIO = 0.2;
const EVENT_LINES_PAGE_SIZE = 1000;
const EVENT_LINES_INFLIGHT_MAX = 50;
const PERCENT_PRECISION_TOLERANCE = 1e-6;
const inflightFetches = new Map<string, InflightFetchEntry>();
const requestClientIds = new WeakMap<SupabaseClient, number>();
let nextRequestClientId = 1;
const inflightAttempts = new Map<string, number>();

const normaliseDate = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length < 10) return null;
  return trimmed.slice(0, 10);
};

const denormaliseRoomKey = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed === '__GENERAL__') return 'GENERAL';
  if (trimmed === '__SUMMARY__') return 'SUMMARY';
  return trimmed;
};

const normaliseUsageDate = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 10);
};

type EventLinesCursor = {
  usageRoomDate: string | null;
  lineNumber: number;
  eventLineId: string;
};

const buildEventLinesKeysetFilter = (cursor: EventLinesCursor | null, dateYmd: string | null): string | null => {
  if (!cursor) {
    return dateYmd ? `usage_room_date.eq.${dateYmd},usage_room_date.is.null` : null;
  }

  const lineNumber = Number.isFinite(cursor.lineNumber) ? Math.trunc(cursor.lineNumber) : 0;
  const id = cursor.eventLineId;

  if (cursor.usageRoomDate) {
    if (dateYmd) {
      return [
        `and(usage_room_date.eq.${dateYmd},line_number.gt.${lineNumber})`,
        `and(usage_room_date.eq.${dateYmd},line_number.eq.${lineNumber},event_line_id.gt.${id})`,
        'usage_room_date.is.null',
      ].join(',');
    }

    return [
      `usage_room_date.gt.${cursor.usageRoomDate}`,
      `and(usage_room_date.eq.${cursor.usageRoomDate},line_number.gt.${lineNumber})`,
      `and(usage_room_date.eq.${cursor.usageRoomDate},line_number.eq.${lineNumber},event_line_id.gt.${id})`,
      'usage_room_date.is.null',
    ].join(',');
  }

  return [
    `and(usage_room_date.is.null,line_number.gt.${lineNumber})`,
    `and(usage_room_date.is.null,line_number.eq.${lineNumber},event_line_id.gt.${id})`,
  ].join(',');
};

const safeNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const resolveEventLinesTimeoutMs = (value?: number): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.max(1, Math.trunc(value));
  }
  return DEFAULT_EVENT_LINES_TIMEOUT_MS;
};

type RetryConfig = {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  jitterRatio: number;
};

const AUTH_ERROR_CODES = new Set(['401', '403', '42501', 'PGRST301', 'PGRST302', 'PGRST303']);

const resolveRetryConfig = (options?: FetchEventLinesRetryOptions): RetryConfig => {
  const maxRetries =
    typeof options?.maxRetries === 'number' && Number.isFinite(options.maxRetries)
      ? Math.max(0, Math.trunc(options.maxRetries))
      : DEFAULT_EVENT_LINES_MAX_RETRIES;
  const baseDelayMs =
    typeof options?.baseDelayMs === 'number' && Number.isFinite(options.baseDelayMs)
      ? Math.max(0, Math.trunc(options.baseDelayMs))
      : DEFAULT_EVENT_LINES_RETRY_BASE_MS;
  const maxDelayMs =
    typeof options?.maxDelayMs === 'number' && Number.isFinite(options.maxDelayMs)
      ? Math.max(baseDelayMs, Math.trunc(options.maxDelayMs))
      : DEFAULT_EVENT_LINES_RETRY_MAX_MS;
  const jitterRatio =
    typeof options?.jitterRatio === 'number' && Number.isFinite(options.jitterRatio)
      ? Math.max(0, Math.min(1, options.jitterRatio))
      : DEFAULT_EVENT_LINES_RETRY_JITTER_RATIO;
  return { maxRetries, baseDelayMs, maxDelayMs, jitterRatio };
};

const resolveErrorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;
  const raw = (error as { status?: unknown; statusCode?: unknown }).status ?? (error as { statusCode?: unknown }).statusCode;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
};

const resolveErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object' || !('code' in error)) return null;
  const code = (error as { code?: unknown }).code;
  if (code == null) return null;
  return String(code);
};

const resolveErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '');
  }
  return String(error ?? '');
};

const resolveErrorName = (error: unknown): string => {
  if (error instanceof Error && error.name) return error.name;
  if (error && typeof error === 'object' && 'name' in error) {
    const name = (error as { name?: unknown }).name;
    if (name) return String(name);
  }
  return 'Error';
};

const isAuthOrPermissionError = (error: unknown): boolean => {
  const status = resolveErrorStatus(error);
  if (status === 401 || status === 403) return true;
  const code = resolveErrorCode(error);
  if (code && AUTH_ERROR_CODES.has(code)) return true;
  const message = resolveErrorMessage(error).toLowerCase();
  return (
    message.includes('permission') ||
    message.includes('not authorized') ||
    message.includes('jwt') ||
    message.includes('token') ||
    message.includes('rls')
  );
};

const isNetworkError = (error: unknown): boolean => {
  const message = resolveErrorMessage(error).toLowerCase();
  if (error instanceof TypeError && message.includes('fetch')) return true;
  return (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('connection') ||
    message.includes('ecconnreset') ||
    message.includes('timeout')
  );
};

const isRetryableError = (error: unknown): boolean => {
  if (error instanceof AbortError) return false;
  if (error instanceof TimeoutError) return true;
  if (isAuthOrPermissionError(error)) return false;
  const status = resolveErrorStatus(error);
  if (status && (status >= 500 || status === 429)) return true;
  return isNetworkError(error);
};

const resolveRetryDelayMs = (retryIndex: number, config: RetryConfig): number => {
  const base = config.baseDelayMs * Math.pow(2, retryIndex);
  if (!base) return 0;
  const jitter =
    config.jitterRatio > 0
      ? 1 + (Math.random() * 2 - 1) * config.jitterRatio
      : 1;
  const value = Math.max(0, Math.round(base * jitter));
  return Math.min(config.maxDelayMs, value);
};

const waitForRetry = (delayMs: number, signal: AbortSignal | null, context: { key?: string; correlationId?: string }) => {
  if (!delayMs) return Promise.resolve();
  if (signal?.aborted) {
    return Promise.reject(
      new AbortError('Request aborted', { key: context.key, correlationId: context.correlationId }),
    );
  }
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      reject(new AbortError('Request aborted', { key: context.key, correlationId: context.correlationId }));
    };
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
};

const buildEventLinesRequestMeta = (eventId: string, dateYmd: string | null): OrderWorkspaceRequestMeta => ({
  eventId,
  usageDate: dateYmd,
  scope: dateYmd ? 'date' : 'all',
});

const coerceFetchEventLinesError = (error: unknown): FetchEventLinesError => {
  if (error instanceof TimeoutError || error instanceof AbortError) return error;
  // Preserve abort-like errors (e.g. DOMException, or Supabase-wrapped abort) so UI can treat as cancelled, not real error.
  if (isAbortLikeError(error)) return error as FetchEventLinesError;
  if (error && typeof error === 'object' && 'message' in error) {
    return error as PostgrestError;
  }
  return {
    message: 'Unknown error',
    details: '',
    hint: '',
    code: 'UNKNOWN',
    name: 'UnknownError',
  } as PostgrestError;
};

const nextInflightAttempt = (key: string): number => {
  const next = (inflightAttempts.get(key) ?? 0) + 1;
  inflightAttempts.set(key, next);
  return next;
};

const buildInflightKey = (eventId: string, dateYmd: string | null): string => `${eventId}::${dateYmd ?? 'ALL'}`;

type InflightPruneResult = {
  expiredKeys: string[];
  trimmedKeys: string[];
};

const pruneInflightEntries = (now: number, maxAgeMs: number, maxEntries: number): InflightPruneResult => {
  const expiredKeys: string[] = [];
  const trimmedKeys: string[] = [];

  if (maxAgeMs > 0) {
    for (const [key, entry] of inflightFetches.entries()) {
      if (now - entry.startedAt > maxAgeMs) {
        expiredKeys.push(key);
      }
    }
  }

  for (const key of expiredKeys) {
    inflightFetches.delete(key);
    inflightAttempts.delete(key);
  }

  if (maxEntries > 0 && inflightFetches.size > maxEntries) {
    const entries = [...inflightFetches.entries()].sort((a, b) => a[1].startedAt - b[1].startedAt);
    const removeCount = inflightFetches.size - maxEntries;
    for (let i = 0; i < removeCount; i += 1) {
      const key = entries[i]?.[0];
      if (!key) continue;
      if (inflightFetches.delete(key)) {
        inflightAttempts.delete(key);
        trimmedKeys.push(key);
      }
    }
  }

  return { expiredKeys, trimmedKeys };
};

export const __eventLinesInflightDebug = {
  getSnapshot: () => ({
    inflightCount: inflightFetches.size,
    attemptsCount: inflightAttempts.size,
    inflightKeys: [...inflightFetches.keys()],
  }),
  clear: () => {
    inflightFetches.clear();
    inflightAttempts.clear();
  },
};

registerSessionCleanup(() => __eventLinesInflightDebug.clear());

export async function fetchEventLineDiscountContext(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
}): Promise<{ context: EventLineDiscountContext; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  return revisionedRead(supabase, { eventId: params.eventId }, 'discount-context', ["lines","financial"], async () => {
  const { eventId } = params;
  const empty: EventLineDiscountContext = {
    ready: true,
    discountableLineIds: [],
    totalPre: 0,
    weightedPercent: null,
    uniformPercent: null,
    discountableCount: 0,
    lineCount: 0,
    smartCapLineCount: 0,
    smartMaxDiscountPct: null,
    smartOverCapLineCount: 0,
    smartMaxOverCapPct: 0,
  };
  if (!eventId) return { context: empty, error: null };

  let result: any = await supabase
    .from('event_lines')
    .select('event_line_id,qty,unit_price,charge_days,discount,not_discountable,product_id,item_id,smart_max_discount_pct,smart_discount_exception_status')
    .eq('event_id', eventId);
  if (result.error && isSmartPricingSchemaUnavailable(result.error)) {
    result = await supabase
      .from('event_lines')
      .select('event_line_id,qty,unit_price,charge_days,discount,not_discountable,product_id,item_id')
      .eq('event_id', eventId);
  }
  const { data, error } = result;

  if (error) return { context: empty, error };

  const rows = Array.isArray(data) ? data : [];
  const discountableLineIds: string[] = [];
  let totalPre = 0;
  let weightedSum = 0;
  let discountableCount = 0;
  const percentKeys = new Set<number>();
  let smartCapLineCount = 0;
  let smartMaxDiscountPct: number | null = null;
  let smartOverCapLineCount = 0;
  let smartMaxOverCapPct = 0;

  for (const row of rows) {
    const id = row?.event_line_id != null ? String(row.event_line_id) : null;
    const notDiscountable = !!(row as any)?.not_discountable;
    if (!notDiscountable && id) {
      discountableLineIds.push(id);
    }
    if (!notDiscountable) {
      discountableCount += 1;
      const qty = safeNumber((row as any)?.qty ?? null) ?? 0;
      const unit = safeNumber((row as any)?.unit_price ?? null) ?? 0;
      const charge = safeNumber((row as any)?.charge_days ?? null);
      const chargeDays = charge != null ? charge : 1;
      const base = Math.max(0, qty * unit * chargeDays);
      totalPre += base;
      const discountRaw = safeNumber((row as any)?.discount ?? null) ?? 0;
      const discountPct = Math.min(Math.max(discountRaw, 0), 100);
      percentKeys.add(discountPct);
      weightedSum += base * discountPct;
      const smartCap = safeNumber((row as any)?.smart_max_discount_pct ?? null);
      if (smartCap != null) {
        const normalizedCap = Math.min(Math.max(smartCap, 0), 100);
        smartCapLineCount += 1;
        smartMaxDiscountPct = smartMaxDiscountPct == null ? normalizedCap : Math.min(smartMaxDiscountPct, normalizedCap);
        if (discountPct - normalizedCap > PERCENT_PRECISION_TOLERANCE) {
          smartOverCapLineCount += 1;
          smartMaxOverCapPct = Math.max(smartMaxOverCapPct, discountPct - normalizedCap);
        }
      }
    }
  }

  const uniformPercent = percentKeys.size === 1 ? Number([...percentKeys][0]) : null;
  const weightedPercent = totalPre > 0 ? weightedSum / totalPre : uniformPercent;

  return {
    context: {
      ready: true,
      discountableLineIds,
      totalPre,
      weightedPercent: weightedPercent != null ? weightedPercent : null,
      uniformPercent,
      discountableCount,
      lineCount: rows.length,
      smartCapLineCount,
      smartMaxDiscountPct,
      smartOverCapLineCount,
      smartMaxOverCapPct,
    },
    error: null,
  };
  });
}

export async function fetchEventLines(options: FetchEventLinesOptions): Promise<FetchEventLinesResult> {
  const supabase = options.supabase ?? getSupabaseClient();
  return revisionedRead(supabase, { eventId: options.eventId }, `lines:${options.dateYmd ?? 'all'}`, ["lines","rooms","reference"], async () => {
  const { eventId } = options;
  if (!eventId) {
    return { lines: [], error: null };
  }

  const dateYmd = normaliseDate(options?.dateYmd ?? null);
  if (!requestClientIds.has(supabase)) requestClientIds.set(supabase, nextRequestClientId++);
  const cacheKey = `${requestClientIds.get(supabase)}:${buildInflightKey(eventId, dateYmd)}`;
  const startedAt = Date.now();
  const correlationId = options.correlationId ?? createOrderWorkspaceCorrelationId('event-lines');
  const timeoutMs = resolveEventLinesTimeoutMs(options?.timeoutMs);
  const retryConfig = resolveRetryConfig(options.retry);
  const requestMeta = buildEventLinesRequestMeta(eventId, dateYmd);

  // Explicit list to keep the workspace payload deterministic and small.
  // NOTE: Avoid nested joins (e.g., event_rooms inside event_room_access) as they trigger
  // RLS policy evaluation which can cause significant slowdowns even for queries returning 0 rows.
  // Room name is already available on event_room_access.room_name - no need to join event_rooms.
  // Extended item/product joins to support custom column configurations (brand, model, manufacturer, etc.)
  const SELECT_CLAUSE = [
    'event_line_id',
    'event_id',
    'action',
    'kind',
    'item_code',
    'product_code',
    'product_id',
    'item_id',
    'section_id',
    'description',
    'note',
    'qty',
    'charge_days',
    'unit_price',
    'discount',
    'r2_allow_discount',
    'r2_discount_amount',
    'r2_pre_discount_amount',
    'r2_net_amount',
    'r2_net_discount_pct',
    'smart_unit_price',
    'smart_max_discount_pct',
    'smart_pricing_snapshot_id',
    'smart_pricing_reason',
    'smart_pricing_signals',
    'smart_discount_exception_status',
    'line_number',
    'not_discountable',
    'non_printable',
    'section_show_subtotal',
    'updated_at',
    'event_room_access_id',
    'room_key',
    'usage_room_date',
    'start_at',
    'end_at',
    'fill_date',
    'fill_time',
    'return_date',
    'return_time',
    'kit_anchor',
    'kit_component',
    'kit_instance_id',
    'kit_item_id',
    'kit_component_id',
    'kit_parent_event_line_id',
    'kit_label',
    'kit_pricing_mode',
    'r2_contract_line_iid',
    'source_charge_days_authoritative',
    'quick_turn',
    // Server-assigned rollup group ID for "All" view grouping
    'rollup_group_id',
    // PostgreSQL xmin system column as row_version for optimistic concurrency (Step 7)
    // Note: Supabase may not expose xmin directly; this is a placeholder for future implementation
    // 'xmin::text as row_version',
    // Extended items JOIN with make/model/manufacturer columns for custom column support
    'items!event_lines_item_id_fkey(item_id,item_type_id,item_code,name_internal,category_id,default_charge_days,brand,mpn,model_name,model_number,manufacturer_name,replacement_cost,weight,length,width,height,can_rent,can_sell,is_bulk,has_unlimited_availability)',
    // Extended products JOIN with additional columns
    'products(product_id,name_public,short_description,long_description,product_code,category_id,default_charge_days,default_item_id,uom,status,visible_to_client)',
    // Extended event_room_access JOIN
    'event_room_access(id,event_room_id,room_name,room_type,access_date,access_start_time,access_end_time,access_notes)',
    // Global sections via section_id (event_lines use sections, not order_sections)
    'sections(section_id,name,sort_order)',
    // Custom fields storage
    'event_line_custom_fields(column_key,field_value)',
  ].join(',');
  const SELECT_CLAUSE_LEGACY = SELECT_CLAUSE
    .split(',')
    .filter(column => !column.trim().startsWith('smart_'))
    .join(',');
  const SELECT_CLAUSE_WITHOUT_KIT_PRICING = SELECT_CLAUSE
    .split(',')
    .filter(column => column.trim() !== 'kit_pricing_mode')
    .join(',');
  const SELECT_CLAUSE_LEGACY_WITHOUT_KIT_PRICING = SELECT_CLAUSE_LEGACY
    .split(',')
    .filter(column => column.trim() !== 'kit_pricing_mode')
    .join(',');
  const isKitPricingModeSchemaUnavailable = (error: unknown): boolean => {
    const message = error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : String(error ?? '');
    return message.includes('kit_pricing_mode');
  };

  const logEventLines = (entry: Omit<Parameters<typeof logOrderWorkspaceEvent>[0], 'correlationId' | 'request'>, level?: 'info' | 'warn' | 'error') => {
    logOrderWorkspaceEvent(
      {
        correlationId,
        request: requestMeta,
        ...entry,
      },
      level ?? 'info',
    );
  };

  const maxAgeMs = timeoutMs * 2;
  const pruneResult = pruneInflightEntries(Date.now(), maxAgeMs, EVENT_LINES_INFLIGHT_MAX);
  if (pruneResult.expiredKeys.length || pruneResult.trimmedKeys.length) {
    logEventLines(
      {
        event: 'eventLines.inflight_prune',
        inflightCount: inflightFetches.size,
        inflightExpired: pruneResult.expiredKeys.length || undefined,
        inflightTrimmed: pruneResult.trimmedKeys.length || undefined,
        inflightMax: EVENT_LINES_INFLIGHT_MAX,
        inflightMaxAgeMs: maxAgeMs,
      },
      pruneResult.trimmedKeys.length ? 'error' : 'warn',
    );
  }

  const inflight = inflightFetches.get(cacheKey);
  if (inflight) {
    if (!inflight.dedupeLogged) {
      inflight.dedupeLogged = true;
      inflight.dedupeCorrelationIds = new Set([inflight.correlationId, correlationId]);
      logEventLines(
        {
          event: 'eventLines.inflight_dedupe',
          inflightCorrelationId: inflight.correlationId,
          correlationIds: Array.from(inflight.dedupeCorrelationIds),
        },
        'warn',
      );
    } else if (inflight.dedupeCorrelationIds && !inflight.dedupeCorrelationIds.has(correlationId)) {
      inflight.dedupeCorrelationIds.add(correlationId);
    }
    return inflight.promise;
  }

  const inflightAttempt = nextInflightAttempt(cacheKey);

  const runAttempt = async (retryIndex: number) => {
    const attemptNumber = retryIndex + 1;
    const attemptStartedAt = Date.now();
    let loggedTimeout = false;
    let loggedAbort = false;

    logEventLines(
      {
        event: 'eventLines.fetch_start',
        attempt: attemptNumber,
        retry: retryIndex,
        timeoutMs,
      },
      'info',
    );

    try {
      const result = await withTimeoutAbortable(
        async ({ signal }): Promise<FetchEventLinesResult> => {
          // Prefer the guarded, normalized workspace snapshot. It avoids
          // sequential 1,000-row PostgREST pages and repeated row-level RLS
          // checks on large imported events. Keep the keyset path below as a
          // rollout fallback while older databases refresh their schema cache.
          if (typeof (supabase as SupabaseClient & { rpc?: unknown }).rpc === 'function') {
            let snapshotQuery = supabase.rpc(
              'event_items_workspace_snapshot' as never,
              {
                p_event_id: eventId,
                p_usage_room_date: dateYmd,
              } as never,
            );
            if (signal) {
              const maybeAbortable = snapshotQuery as typeof snapshotQuery & {
                abortSignal?: (abortSignal: AbortSignal) => typeof snapshotQuery;
              };
              if (typeof maybeAbortable.abortSignal === 'function') {
                snapshotQuery = maybeAbortable.abortSignal(signal);
              }
            }

            const { data: snapshotData, error: snapshotError } = await markRequest(snapshotQuery, 'read');
            if (!snapshotError) {
              const hydratedLines = hydrateEventItemsWorkspaceSnapshot(snapshotData);
              if (hydratedLines) {
                logEventLines({
                  event: 'eventLines.workspace_snapshot_success',
                  attempt: attemptNumber,
                  retry: retryIndex,
                  rowCount: hydratedLines.length,
                  durationMs: Date.now() - attemptStartedAt,
                });
                return { lines: hydratedLines, error: null };
              }
              logEventLines(
                {
                  event: 'eventLines.workspace_snapshot_invalid',
                  attempt: attemptNumber,
                  retry: retryIndex,
                  durationMs: Date.now() - attemptStartedAt,
                },
                'warn',
              );
            } else if (isMissingEventItemsWorkspaceSnapshotRpc(snapshotError)) {
              logEventLines(
                {
                  event: 'eventLines.workspace_snapshot_fallback',
                  attempt: attemptNumber,
                  retry: retryIndex,
                  durationMs: Date.now() - attemptStartedAt,
                  errorCode: snapshotError.code ?? null,
                  errorMessage: snapshotError.message,
                },
                'warn',
              );
            } else {
              return { lines: [], error: snapshotError };
            }
          }

          const lines: EventLineRecord[] = [];
          let cursor: EventLinesCursor | null = null;
          let smartPricingSelectUnavailable = false;
          let kitPricingModeSelectUnavailable = false;

          while (true) {
            const selectClause = smartPricingSelectUnavailable
              ? kitPricingModeSelectUnavailable
                ? SELECT_CLAUSE_LEGACY_WITHOUT_KIT_PRICING
                : SELECT_CLAUSE_LEGACY
              : kitPricingModeSelectUnavailable
                ? SELECT_CLAUSE_WITHOUT_KIT_PRICING
                : SELECT_CLAUSE;
            // Step 2: Order by stable keys only (not updated_at). Deterministic tie-breaker for same (usage_room_date, line_number).
            let query = supabase
              .from('event_lines')
              .select(selectClause)
              .eq('event_id', eventId)
              .order('usage_room_date', { ascending: true, nullsFirst: false })
              .order('line_number', { ascending: true, nullsFirst: false })
              .order('event_line_id', { ascending: true });

            const keysetFilter = buildEventLinesKeysetFilter(cursor, dateYmd);
            if (keysetFilter) {
              query = query.or(keysetFilter);
            }

            query = query.limit(EVENT_LINES_PAGE_SIZE);

            if (signal) {
              const maybeAbortable = query as typeof query & { abortSignal?: (signal: AbortSignal) => typeof query };
              if (typeof maybeAbortable.abortSignal === 'function') {
                query = maybeAbortable.abortSignal(signal);
              }
            }

            const { data, error } = await query;
            if (error) {
              if (!smartPricingSelectUnavailable && isSmartPricingSchemaUnavailable(error)) {
                smartPricingSelectUnavailable = true;
                continue;
              }
              if (!kitPricingModeSelectUnavailable && isKitPricingModeSchemaUnavailable(error)) {
                kitPricingModeSelectUnavailable = true;
                continue;
              }
              return { lines: [], error };
            }

            const pageLines = Array.isArray(data) ? (data as unknown as EventLineRecord[]) : [];
            lines.push(...pageLines);
            if (pageLines.length < EVENT_LINES_PAGE_SIZE) break;

            const last = pageLines[pageLines.length - 1] as any;
            const lastId = typeof last?.event_line_id === 'string' ? last.event_line_id : null;
            if (!lastId) break;

            cursor = {
              usageRoomDate: normaliseUsageDate(last?.usage_room_date ?? null),
              lineNumber: safeNumber(last?.line_number ?? null) ?? 0,
              eventLineId: lastId,
            };
          }

          return { lines, error: null };
        },
        {
          timeoutMs,
          key: cacheKey,
          correlationId,
          signal: options.signal ?? null,
          operation: 'read',
          logger: (entry) => {
            if (entry.event === 'timeout') {
              loggedTimeout = true;
              logEventLines(
                {
                  event: 'eventLines.fetch_timeout',
                  attempt: attemptNumber,
                  retry: retryIndex,
                  timeoutMs: entry.timeoutMs ?? timeoutMs,
                  durationMs: entry.durationMs ?? Date.now() - attemptStartedAt,
                },
                'warn',
              );
            } else if (entry.event === 'abort') {
              loggedAbort = true;
              logEventLines(
                {
                  event: 'eventLines.fetch_abort',
                  attempt: attemptNumber,
                  retry: retryIndex,
                  durationMs: entry.durationMs ?? Date.now() - attemptStartedAt,
                },
                'warn',
              );
            }
          },
        },
      );
      const durationMs = Date.now() - attemptStartedAt;
      if (result.lines?.length) {
        logEventLines({
          event: 'eventLines.raw_server_sample',
          sample: result.lines.slice(0, 20).map((l: EventLineRecord) => ({
            id: l.event_line_id?.slice(0, 24),
            charge_days: l.charge_days,
            discount: l.discount,
          })),
        });
      }
      return { lines: result.lines, error: result.error, durationMs, loggedTimeout, loggedAbort };
    } catch (error) {
      const durationMs = Date.now() - attemptStartedAt;
      if (error instanceof TimeoutError && !loggedTimeout) {
        logEventLines(
          {
            event: 'eventLines.fetch_timeout',
            attempt: attemptNumber,
            retry: retryIndex,
            timeoutMs: error.timeoutMs ?? timeoutMs,
            durationMs,
          },
          'warn',
        );
        loggedTimeout = true;
      }
      if ((error instanceof AbortError || isAbortLikeError(error)) && !loggedAbort) {
        logEventLines(
          {
            event: 'eventLines.fetch_abort',
            attempt: attemptNumber,
            retry: retryIndex,
            durationMs,
          },
          'info',
        );
        loggedAbort = true;
      }
      return { lines: [], error, durationMs, loggedTimeout, loggedAbort };
    }
  };

  const run = (async (): Promise<FetchEventLinesResult> => {
    let lastError: unknown = null;
    for (let retryIndex = 0; retryIndex <= retryConfig.maxRetries; retryIndex += 1) {
      const attemptResult = await runAttempt(retryIndex);
      if (!attemptResult.error) {
        logEventLines(
          {
            event: 'eventLines.fetch_success',
            attempt: retryIndex + 1,
            retry: retryIndex,
            durationMs: Date.now() - startedAt,
            rowCount: attemptResult.lines.length,
          },
          'info',
        );
        return { lines: attemptResult.lines, error: null };
      }

      lastError = attemptResult.error;
      const retryable = isRetryableError(attemptResult.error);
      const retryDelayMs =
        retryable && retryIndex < retryConfig.maxRetries
          ? resolveRetryDelayMs(retryIndex, retryConfig)
          : 0;

      const isAbort = isAbortLikeError(attemptResult.error);
      if (!(attemptResult.error instanceof TimeoutError || attemptResult.error instanceof AbortError || isAbort)) {
        logEventLines(
          {
            event: 'eventLines.fetch_error',
            attempt: retryIndex + 1,
            retry: retryIndex,
            durationMs: attemptResult.durationMs,
            retryable,
            retryDelayMs: retryDelayMs || undefined,
            errorName: resolveErrorName(attemptResult.error),
            errorStatus: resolveErrorStatus(attemptResult.error),
            errorCode: resolveErrorCode(attemptResult.error),
            errorMessage: resolveErrorMessage(attemptResult.error),
          },
          retryable ? 'warn' : 'error',
        );
      } else if (isAbort) {
        logEventLines(
          {
            event: 'eventLines.fetch_abort',
            attempt: retryIndex + 1,
            retry: retryIndex,
            durationMs: attemptResult.durationMs,
          },
          'info',
        );
      }

      if (!retryable || retryIndex >= retryConfig.maxRetries) {
        break;
      }

      try {
        await waitForRetry(retryDelayMs, options.signal ?? null, { key: cacheKey, correlationId });
      } catch (error) {
        lastError = error;
        break;
      }
    }
    return { lines: [], error: lastError ? coerceFetchEventLinesError(lastError) : null };
  })();

  inflightFetches.set(cacheKey, {
    promise: run,
    startedAt,
    correlationId,
    attempt: inflightAttempt,
  });
  try {
    return await run;
  } catch (error) {
    return { lines: [], error: coerceFetchEventLinesError(error) };
  } finally {
    const current = inflightFetches.get(cacheKey);
    if (current?.promise === run) {
      inflightFetches.delete(cacheKey);
      inflightAttempts.delete(cacheKey);
    }
  }
  });
}

/**
 * Build UPDATE payload from _editedFields only (edited-fields pick).
 * Used when eventLineId is set and _editedFields is provided so we never send a full row on update.
 * Only includes line_number when _editedFields contains 'line_order' or 'line_number' (reorder).
 * Normalization is strictly per-field: payload keys are only those in allowedDbColumns (from editedFields);
 * no extra columns can be added — we iterate only over allowedDbColumns and the switch has default: continue.
 * Exported for unit tests.
 */
export function buildUpdatePayloadFromEditedFields(
  input: EventLineUpsertInput,
  editedFields: string[]
): Record<string, unknown> {
  const allowedDbColumns = editedFieldsToDbColumns(editedFields);
  const includeLineNumber = editedFields.some(
    (k) => k === 'line_order' || k === 'line_number'
  );
  const payload: Record<string, unknown> = {};

  for (const dbCol of allowedDbColumns) {
    if (dbCol === 'line_number' && !includeLineNumber) continue;

    let value: unknown;
    switch (dbCol) {
      case 'charge_days':
        // 0 is valid (no charge); only use null when truly missing
        value = safeNumber(input.chargeDays) ?? input.chargeDays ?? null;
        break;
      case 'unit_price':
        value = safeNumber(input.unitPrice) ?? input.unitPrice ?? null;
        break;
      case 'discount':
        value = safeNumber(input.discountPct) ?? input.discountPct ?? null;
        break;
      case 'line_number':
        value = safeNumber(input.lineNumber) != null ? Math.max(1, Math.trunc(safeNumber(input.lineNumber)!)) : null;
        break;
      case 'room_key':
        value = denormaliseRoomKey(input.roomKey) ?? 'GENERAL';
        break;
      case 'usage_room_date':
        value = normaliseUsageDate(input.usageRoomDate) ?? normaliseUsageDate(new Date().toISOString());
        break;
      case 'event_room_access_id':
        value = input.eventRoomAccessId ?? null;
        break;
      case 'product_id':
        value = input.productId ?? null;
        break;
      case 'item_id':
        value = input.itemId ?? null;
        break;
      case 'section_id':
        value = input.sectionId ?? null;
        break;
      case 'description':
        value = input.description ?? null;
        break;
      case 'note':
        value = input.note ?? null;
        break;
      case 'qty':
        value = safeNumber(input.qty) != null ? Math.max(0, Math.trunc(safeNumber(input.qty)!)) : null;
        break;
      case 'action':
        value = input.action != null ? normaliseLineAction(input.action) : null;
        break;
      case 'kind':
        value = input.kind ?? null;
        break;
      case 'not_discountable':
        value = input.notDiscountable ?? null;
        break;
      case 'non_printable':
        value = input.nonPrintable ?? null;
        break;
      case 'section_show_subtotal':
        value = input.sectionShowSubtotal ?? null;
        break;
      case 'fill_date':
        value = input.fill_date ?? null;
        break;
      case 'fill_time':
        value = input.fill_time ?? null;
        break;
      case 'return_date':
        value = input.return_date ?? null;
        break;
      case 'return_time':
        value = input.return_time ?? null;
        break;
      case 'kit_anchor':
        value = input.kitAnchor ?? null;
        break;
      case 'kit_component':
        value = input.kitComponent ?? null;
        break;
      case 'kit_instance_id':
        value = input.kitInstanceId ?? null;
        break;
      case 'kit_item_id':
        value = input.kitItemId ?? null;
        break;
      case 'kit_component_id':
        value = input.kitComponentId ?? null;
        break;
      case 'kit_parent_event_line_id':
        value = input.kitParentEventLineId ?? null;
        break;
      case 'kit_label':
        value = input.kitLabel ?? null;
        break;
      case 'kit_pricing_mode':
        value = input.kitPricingMode != null ? normaliseKitPricingMode(input.kitPricingMode) : null;
        break;
      default:
        continue;
    }
    if (value !== undefined) {
      payload[dbCol] = value;
    }
  }
  if (allowedDbColumns.includes('charge_days')) {
    // A user-entered daily value replaces the imported source allocation.
    // Both order-workspace layouts persist through this helper.
    payload.source_charge_days_authoritative = false;
  }
  return payload;
}

export async function upsertEventLine(input: EventLineUpsertInput): Promise<EventLineUpsertResult> {
  const {
    supabase: supabaseOverride,
    eventId,
    eventLineId,
    eventRoomAccessId,
    roomKey,
    usageRoomDate,
    action,
    kind,
    productId,
    itemId,
    sectionId,
    description,
    qty,
    chargeDays,
    unitPrice,
    discountPct,
    notDiscountable,
    nonPrintable,
    sectionShowSubtotal,
    lineNumber,
    fill_date,
    fill_time,
    ship_date,
    ship_time,
    return_date,
    return_time,
    kitAnchor,
    kitComponent,
    kitInstanceId,
    kitItemId,
    kitComponentId,
    kitParentEventLineId,
    kitLabel,
    kitPricingMode,
    _instrumentWrite,
    _editedFields,
  } = input;

  const strictPayloadEnabled =
    typeof import.meta !== 'undefined' &&
    (import.meta.env?.DEV === true || import.meta.env?.VITE_ITEMS_STRICT_PAYLOAD === 'true');

  const supabase = supabaseOverride ?? getSupabaseClient();

  if (!eventId) {
    return { eventLineId: eventLineId ?? null, updatedAt: null, error: null };
  }

  const isUpdateWithEditedFields = Boolean(
    eventLineId && _editedFields && _editedFields.length > 0
  );

  // Fail hard in dev when UPDATE is attempted without _editedFields so we never silently fall back to full-row update.
  if (
    eventLineId &&
    (!_editedFields || _editedFields.length === 0) &&
    strictPayloadEnabled
  ) {
    const err = new Error(
      '[ItemsPanel] UPDATE requires non-empty _editedFields; cannot fall back to full-row update. Pass the fields the user actually edited.'
    ) as Error & { code?: string };
    err.code = 'MISSING_EDITED_FIELDS_ON_UPDATE';
    throw err;
  }

  // Dev-only: updates must send client read version (updatedAt) for optimistic concurrency.
  const includesActionEditedUpdate =
    eventLineId &&
    Array.isArray(_editedFields) &&
    _editedFields.includes('action');
  if (
    eventLineId &&
    _editedFields &&
    _editedFields.length > 0 &&
    strictPayloadEnabled &&
    (input.updatedAt == null || input.updatedAt === '') &&
    !includesActionEditedUpdate
  ) {
    const err = new Error(
      '[ItemsPanel] UPDATE with existing event_line_id requires input.updatedAt (client read version) for optimistic concurrency.'
    ) as Error & { code?: string };
    err.code = 'MISSING_CLIENT_READ_VERSION_ON_UPDATE';
    throw err;
  }

  let payload: Record<string, any>;

  if (isUpdateWithEditedFields) {
    payload = buildUpdatePayloadFromEditedFields(input, _editedFields!) as Record<string, any>;
  } else {
    payload = {
      event_id: eventId,
      room_key: denormaliseRoomKey(roomKey) ?? 'GENERAL',
      usage_room_date: normaliseUsageDate(usageRoomDate) ?? null,
    };

    if (eventRoomAccessId !== undefined) {
      payload.event_room_access_id = eventRoomAccessId || null;
    }

    if (productId !== undefined) payload.product_id = productId || null;
    if (itemId !== undefined) payload.item_id = itemId || null;
    if (sectionId !== undefined) payload.section_id = sectionId || null;
    if (description !== undefined) payload.description = description || null;

    if (action !== undefined) {
      payload.action = normaliseLineAction(action);
    }

    if (kind !== undefined) {
      payload.kind = kind || null;
    }

    const qtyNumber = safeNumber(qty);
    if (qtyNumber != null) payload.qty = Math.max(0, Math.trunc(qtyNumber));

    const chargeDaysNumber = safeNumber(chargeDays);
    if (chargeDaysNumber != null) payload.charge_days = chargeDaysNumber;

    const unitPriceNumber = safeNumber(unitPrice);
    if (unitPriceNumber != null) payload.unit_price = unitPriceNumber;

    const discountNumber = safeNumber(discountPct);
    if (discountNumber != null) payload.discount = discountNumber;

    const lineNum = safeNumber(lineNumber);
    if (lineNum != null) payload.line_number = Math.max(1, Math.trunc(lineNum));

    if (typeof notDiscountable === 'boolean') {
      payload.not_discountable = notDiscountable;
    }

    if (typeof nonPrintable === 'boolean') {
      payload.non_printable = nonPrintable;
    }

    if (typeof sectionShowSubtotal === 'boolean') {
      payload.section_show_subtotal = sectionShowSubtotal;
    }

    if (kitAnchor !== undefined) payload.kit_anchor = Boolean(kitAnchor);
    if (kitComponent !== undefined) payload.kit_component = Boolean(kitComponent);
    if (kitInstanceId !== undefined) payload.kit_instance_id = kitInstanceId || null;
    if (kitItemId !== undefined) payload.kit_item_id = kitItemId || null;
    if (kitComponentId !== undefined) payload.kit_component_id = kitComponentId || null;
    if (kitParentEventLineId !== undefined) payload.kit_parent_event_line_id = kitParentEventLineId || null;
    if (kitLabel !== undefined) payload.kit_label = kitLabel || null;
    if (kitPricingMode !== undefined) {
      payload.kit_pricing_mode = kitPricingMode != null ? normaliseKitPricingMode(kitPricingMode) : null;
    }

    if (input.r2ContractLineIid !== undefined) {
      payload.r2_contract_line_iid = Array.isArray(input.r2ContractLineIid) ? input.r2ContractLineIid : [];
    }

    const scheduleProvided = [fill_date, fill_time, ship_date, ship_time, return_date, return_time].some((v) => v !== undefined);
    if (scheduleProvided) {
      if (fill_date !== undefined) payload.fill_date = fill_date || null;
      if (fill_time !== undefined) payload.fill_time = fill_time || null;
      if (ship_date !== undefined) payload.ship_date = ship_date || null;
      if (ship_time !== undefined) payload.ship_time = ship_time || null;
      if (return_date !== undefined) payload.return_date = return_date || null;
      if (return_time !== undefined) payload.return_time = return_time || null;
    }

    if (payload.usage_room_date == null) {
      payload.usage_room_date = normaliseUsageDate(new Date().toISOString());
    }
  }

  const clearsR2PricingSnapshot = [
    'qty',
    'charge_days',
    'unit_price',
    'discount',
    'not_discountable',
  ].some((column) => payload[column] !== undefined);
  if (clearsR2PricingSnapshot) {
    payload.r2_allow_discount = null;
    payload.r2_discount_amount = null;
    payload.r2_pre_discount_amount = null;
    payload.r2_net_amount = null;
    payload.r2_net_discount_pct = null;
  }

  if (payload.charge_days !== undefined && isUpdateWithEditedFields) {
    payload.source_charge_days_authoritative = false;
  }

  const payloadColumns = Object.keys(payload);

  // Step 1: Always validate _editedFields when provided — every entry must be a known DB column or UI alias (mapped in one place). Reject unknown keys with UNKNOWN_EDITED_FIELD.
  if (_editedFields && _editedFields.length > 0) {
    try {
      editedFieldsToDbColumns(_editedFields);
    } catch (e: unknown) {
      const err = e as Error & { code?: string };
      if (err.code === 'UNKNOWN_EDITED_FIELD') throw err;
      throw e;
    }
  }

  // Step 1 (enforcement): dev-only strict payload allowlist; payload columns must be subset of _editedFields (mapped to DB)
  if (strictPayloadEnabled && eventLineId && _editedFields && _editedFields.length > 0) {
    const allowedDbColumns = new Set(editedFieldsToDbColumns(_editedFields));
    if (allowedDbColumns.has('charge_days')) {
      allowedDbColumns.add('source_charge_days_authoritative');
    }
    if (['qty', 'charge_days', 'unit_price', 'discount', 'not_discountable'].some((column) => allowedDbColumns.has(column))) {
      allowedDbColumns.add('r2_allow_discount');
      allowedDbColumns.add('r2_discount_amount');
      allowedDbColumns.add('r2_pre_discount_amount');
      allowedDbColumns.add('r2_net_amount');
      allowedDbColumns.add('r2_net_discount_pct');
    }
    const disallowed = payloadColumns.filter((col) => !allowedDbColumns.has(col));
    if (disallowed.length > 0) {
      const err = new Error(
        `[ItemsPanel strict payload] Update payload includes columns that were not edited: ${disallowed.join(', ')}. Allowed for this edit: ${[...allowedDbColumns].join(', ')}. This is an overwrite bug.`
      ) as Error & { code?: string };
      err.code = 'STRICT_PAYLOAD_VIOLATION';
      throw err;
    }
  }

  // Step 1: Instrument write payload (exact Supabase payload, columns, event_line_id, client read version)
  // Step 3: Flag when update includes line_number (non-reorder edits must not change line_number)
  if (_instrumentWrite) {
    _instrumentWrite({
      payloadColumns,
      eventLineId: eventLineId ?? null,
      clientReadVersion: input.updatedAt ?? null,
      payload: { ...payload },
      isUpdate: Boolean(eventLineId),
      payloadIncludesLineNumber: payload.line_number !== undefined,
    });
  }

  // Step 4: Edits use UPDATE only (never upsert/insert for existing rows). New rows take the insert path below.
  if (eventLineId) {
    // Step 5: Optimistic concurrency — WHERE updated_at = client read version; 409 if row changed by someone else.
    let query = supabase.from('event_lines').update(payload).eq('event_line_id', eventLineId);
    if (input.updatedAt && !includesActionEditedUpdate) {
      query = query.eq('updated_at', input.updatedAt);
    }
    const selectCols = 'event_line_id, action, charge_days, discount, line_number, updated_at';
    const result = await markRequest(query.select(selectCols).maybeSingle(), 'mutation', {
      handlesVersionConflict: Boolean(input.updatedAt && !includesActionEditedUpdate),
    });
    let data = result.data;
    let error = result.error;

    const uncertain = error && error.code !== '57014' && (result.status >= 500 || (!error.code
      && /fetch|network|abort|connection/i.test(error.message)));
    if (uncertain) {
      // A disconnected response cannot tell us whether the guarded UPDATE
      // committed. Confirm the desired fields before allowing a retry.
      const columns = [...new Set([...selectCols.split(',').map(column => column.trim()), ...payloadColumns])];
      const readback = await supabase.from('event_lines').select(columns.join(','))
        .eq('event_line_id', eventLineId).maybeSingle();
      const saved = readback.data as Record<string, unknown> | null;
      const matches = !readback.error && saved && payloadColumns.every(column =>
        JSON.stringify(saved[column] ?? null) === JSON.stringify(payload[column] ?? null));
      if (matches) {
        data = saved as typeof data;
        error = null;
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('portal:write-committed'));
      } else {
        return { eventLineId, updatedAt: input.updatedAt ?? null, error: {
          name: 'UncertainSaveError', code: 'PORTAL_WRITE_UNCERTAIN', details: '', hint: '',
          message: 'The save could not be confirmed. Your draft is still here. Check the saved line before trying again.',
        } as PostgrestError };
      }
    }

    if (error) {
      return { eventLineId: eventLineId ?? null, updatedAt: input.updatedAt ?? null, error };
    }

    if (!data) {
    return {
      eventLineId: eventLineId ?? null,
      updatedAt: input.updatedAt ?? null,
      error: {
        message: 'This line was updated in the background. Refresh to continue.',
        name: 'ConcurrencyError',
        details: '',
        hint: null,
        code: '409',
      } as unknown as PostgrestError,
    };
  }

    if (input._instrumentWriteResult && data) {
      const row = data as { event_line_id?: string; action?: string | null; charge_days?: number | null; discount?: number | null; line_number?: number | null; updated_at?: string | null; xmin?: unknown };
      input._instrumentWriteResult({
        event_line_id: row.event_line_id ? String(row.event_line_id) : eventLineId,
        action: row.action ?? null,
        charge_days: row.charge_days ?? null,
        discount: row.discount ?? null,
        line_number: row.line_number ?? null,
        updated_at: row.updated_at ?? null,
        xmin: row.xmin,
      });
    }
    const resolvedId = data?.event_line_id ? String(data.event_line_id) : eventLineId;
    const resolvedUpdatedAt = data?.updated_at ? String(data.updated_at) : input.updatedAt ?? null;
    if (payload.item_id !== undefined) {
      const smartPricingApplyError = await applyLatestEventSmartPricing({ supabase, eventId });
      if (smartPricingApplyError) {
        return { eventLineId: resolvedId, updatedAt: resolvedUpdatedAt, error: smartPricingApplyError };
      }
    }
    if (payload.discount !== undefined || payload.not_discountable !== undefined) {
      const smartPricingRefreshError = await refreshEventSmartPricingExceptions({ supabase, eventId });
      if (smartPricingRefreshError) {
        return { eventLineId: resolvedId, updatedAt: resolvedUpdatedAt, error: smartPricingRefreshError };
      }
    }
    return { eventLineId: resolvedId, updatedAt: resolvedUpdatedAt, error: null };
  }

  // Atomic insert: use RPC so DB assigns next line_number in scope under lock (avoids concurrent-insert unique violations).
  const rpcPayload = { ...payload };
  delete rpcPayload.line_number;
  const { data: rpcData, error } = await markRequest(supabase.rpc('insert_event_line_at_end', {
    p_row: rpcPayload,
  }), 'mutation');

  if (error) {
    return { eventLineId: null, updatedAt: null, error };
  }

  const row = (rpcData as Record<string, unknown> | null) ?? null;
  const resolvedId = row?.event_line_id != null ? String(row.event_line_id) : null;
  const resolvedUpdatedAt = row?.updated_at != null ? String(row.updated_at) : null;
  if (payload.item_id !== undefined) {
    const smartPricingApplyError = await applyLatestEventSmartPricing({ supabase, eventId });
    if (smartPricingApplyError) {
      return { eventLineId: resolvedId, updatedAt: resolvedUpdatedAt, error: smartPricingApplyError };
    }
  }
  if (payload.discount !== undefined || payload.not_discountable !== undefined) {
    const smartPricingRefreshError = await refreshEventSmartPricingExceptions({ supabase, eventId });
    if (smartPricingRefreshError) {
      return { eventLineId: resolvedId, updatedAt: resolvedUpdatedAt, error: smartPricingRefreshError };
    }
  }
  return { eventLineId: resolvedId, updatedAt: resolvedUpdatedAt, error: null };
}

export async function deleteEventLine(
  eventLineId?: string | null,
  options?: { supabase?: SupabaseClient },
): Promise<PostgrestError | null> {
  if (!eventLineId) return null;
  const supabase = options?.supabase ?? getSupabaseClient();

  // The FK constraint event_lines_kit_parent_event_line_id_fkey uses
  // ON DELETE SET NULL so Postgres automatically clears child references.
  const { error } = await supabase.from('event_lines').delete().eq('event_line_id', eventLineId);
  return error ?? null;
}

/**
 * Delete all event_lines matching a scope (room, date, or both).
 *
 * - roomKey only → delete all lines in that room across every date.
 * - usageRoomDate only → delete all lines on that date across every room.
 * - both → delete lines that match EITHER (union), covering the full room and the full date.
 *
 * Returns the count of deleted rows (when the DB reports it) and any error.
 */
export async function deleteEventLinesByScope(
  eventId: string,
  scope: { roomKey?: string | null; usageRoomDate?: string | null },
  options?: { supabase?: SupabaseClient },
): Promise<{ count: number; error: PostgrestError | null }> {
  if (!eventId) return { count: 0, error: null };
  const { roomKey, usageRoomDate } = scope;
  if (!roomKey && !usageRoomDate) return { count: 0, error: null };

  const supabase = options?.supabase ?? getSupabaseClient();

  // When both filters are provided we want an OR (delete lines for the
  // room on *any* date plus lines on the target date in *any* room).
  if (roomKey && usageRoomDate) {
    const { error, count } = await supabase
      .from('event_lines')
      .delete({ count: 'exact' })
      .eq('event_id', eventId)
      .or(`room_key.eq.${roomKey},usage_room_date.eq.${usageRoomDate}`);
    return { count: count ?? 0, error: error ?? null };
  }

  // Single-dimension filter.
  let query = supabase
    .from('event_lines')
    .delete({ count: 'exact' })
    .eq('event_id', eventId);
  if (roomKey) query = query.eq('room_key', roomKey);
  if (usageRoomDate) query = query.eq('usage_room_date', usageRoomDate);

  const { error, count } = await query;
  return { count: count ?? 0, error: error ?? null };
}

/** A line's number and version (updated_at) after a resequence. */
export type EventLineVersion = { eventLineId: string; lineNumber: number; updatedAt: string };

/**
 * Renumbers lines in two passes. The database gives every line it touches a new updated_at
 * (the content-version trigger counts line_number), so the second pass returns each line's
 * version; a caller that keeps the old one would have its next save refused as stale.
 */
export async function resequenceEventLines(
  entries: EventLineSequenceEntry[],
  options?: { supabase?: SupabaseClient },
): Promise<{ error: PostgrestError | null; versions: EventLineVersion[] }> {
  const versions: EventLineVersion[] = [];
  if (!entries.length) return { error: null, versions };
  const supabase = options?.supabase ?? getSupabaseClient();

  // Optimized two-phase update using batched promises to reduce latency.
  // Phase 1 moves rows to a high positive temporary range to clear the unique
  // constraint without leaving invalid negative values if the request is interrupted.
  // Phase 2: Update to final values
  // This ensures we never have two rows with the same line_number in the same scope
  const sortedEntries = [...entries].sort((a, b) => a.lineNumber - b.lineNumber);
  const validEntries = sortedEntries.filter(e => e.eventLineId);

  if (!validEntries.length) return { error: null, versions };

  // Batch size for concurrent updates (balance between speed and not overwhelming the DB)
  const BATCH_SIZE = 20;

  const TEMP_LINE_NUMBER_BASE = 1_000_000_000;

  // Phase 1: Set temporary high positive values in parallel batches
  for (let i = 0; i < validEntries.length; i += BATCH_SIZE) {
    const batch = validEntries.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map((entry, batchIdx) =>
        supabase
          .from('event_lines')
          .update({ line_number: TEMP_LINE_NUMBER_BASE + i + batchIdx + 1 })
          .eq('event_line_id', entry.eventLineId!)
      )
    );
    // Check for errors in this batch
    for (const result of results) {
      if (result.error) return { error: result.error, versions };
    }
  }

  // Phase 2: Update to final values in parallel batches
  for (let i = 0; i < validEntries.length; i += BATCH_SIZE) {
    const batch = validEntries.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(entry =>
        supabase
          .from('event_lines')
          .update({ line_number: Math.max(1, Math.trunc(entry.lineNumber)) })
          .eq('event_line_id', entry.eventLineId!)
          .select('event_line_id,line_number,updated_at')
          .maybeSingle()
      )
    );
    // Check for errors in this batch
    for (const result of results) {
      if (result.error) return { error: result.error, versions };
      const row = result.data as { event_line_id?: string | null; line_number?: number | null; updated_at?: string | null } | null;
      if (row?.event_line_id && row.updated_at) {
        versions.push({ eventLineId: String(row.event_line_id), lineNumber: Number(row.line_number), updatedAt: String(row.updated_at) });
      }
    }
  }

  return { error: null, versions };
}

export async function fetchMaxEventLineNumber(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
  roomKey?: string | null;
  usageRoomDate?: string | null;
  eventRoomAccessId?: string | null;
}): Promise<{ maxLineNumber: number; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId, roomKey, usageRoomDate, eventRoomAccessId } = params;
  if (!eventId) return { maxLineNumber: 0, error: null };

  const denormRoomKey = denormaliseRoomKey(roomKey) ?? 'GENERAL';
  const usageDate = normaliseDate(usageRoomDate);
  let query = supabase
    .from('event_lines')
    .select('line_number')
    .order('line_number', { ascending: false })
    .limit(1)
    .eq('event_id', eventId)
    .eq('room_key', denormRoomKey);

  if (usageDate) query = query.eq('usage_room_date', usageDate);
  if (eventRoomAccessId) query = query.eq('event_room_access_id', eventRoomAccessId);

  const { data, error } = await query;
  if (error) return { maxLineNumber: 0, error };

  const maxLine = Array.isArray(data) && data[0] && data[0].line_number != null ? Number(data[0].line_number) : 0;
  const safeValue = Number.isFinite(maxLine) ? maxLine : 0;
  return { maxLineNumber: safeValue, error: null };
}

async function refreshEventSmartPricingExceptions(params: {
  supabase: SupabaseClient;
  eventId: string | null | undefined;
}): Promise<PostgrestError | null> {
  if (!params.eventId) return null;
  const result = await refreshSmartPricingDiscountException({
    supabase: params.supabase,
    eventId: params.eventId,
  });
  if (result.unavailable) return null;
  return result.error ?? null;
}

async function applyLatestEventSmartPricing(params: {
  supabase: SupabaseClient;
  eventId: string | null | undefined;
}): Promise<PostgrestError | null> {
  if (!params.eventId) return null;
  const result = await applyLatestSmartPricingToEvent({
    supabase: params.supabase,
    eventId: params.eventId,
  });
  if (result.unavailable) return null;
  return result.error ?? null;
}

export async function applyEventLineDiscount(params: {
  supabase?: SupabaseClient;
  eventId: string;
  mode: EventLineDiscountMode;
  percent: number | null;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId, mode, percent } = params;
  let query = supabase.from('event_lines').update({ discount: mode === 'apply' ? percent : 0 }).eq('event_id', eventId).eq('not_discountable', false);
  if (mode === 'clear' && percent != null) {
    query = query.eq('discount', percent);
  }
  const { error } = await query;
  if (error) return error;
  return refreshEventSmartPricingExceptions({ supabase, eventId });
}

export async function findEventLinesAboveChargeDays(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
  chargeDays: number;
}): Promise<{ hasLines: boolean; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId, chargeDays } = params;
  if (!eventId) return { hasLines: false, error: null };
  const { data, error } = await supabase
    .from('event_lines')
    .select('event_line_id')
    .eq('event_id', eventId)
    .gt('charge_days', chargeDays)
    .limit(1);
  if (error) return { hasLines: false, error };
  const hasLines = Array.isArray(data) && data.length > 0;
  return { hasLines, error: null };
}

export async function updateEventLineChargeDays(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
  chargeDays: number;
}): Promise<{ updatedIds: string[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId, chargeDays } = params;
  if (!eventId) return { updatedIds: [], error: null };
  const { data, error } = await supabase
    .from('event_lines')
    .update({
      charge_days: chargeDays,
      source_charge_days_authoritative: false,
    })
    .eq('event_id', eventId)
    .gt('charge_days', chargeDays)
    .select('event_line_id');
  if (error) return { updatedIds: [], error };
  const ids = Array.isArray(data) ? data.map(row => String(row.event_line_id)).filter(Boolean) : [];
  return { updatedIds: ids, error: null };
}

/**
 * Moves whole event lines into another room. Used by the drag-and-drop handoff
 * in the items workspace, where a row is dragged from one room card onto
 * another. Scoped by event_id as well as id so a stale client cannot move lines
 * belonging to a different event.
 */
export async function moveEventLinesToRoom(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
  eventLineIds: string[];
  roomKey: string | null;
}): Promise<{ movedIds: string[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId, eventLineIds, roomKey } = params;
  const ids = Array.from(
    new Set((eventLineIds ?? []).map(id => String(id ?? '').trim()).filter(Boolean)),
  );
  if (!eventId || ids.length === 0) return { movedIds: [], error: null };
  const { data, error } = await supabase
    .from('event_lines')
    .update({ room_key: denormaliseRoomKey(roomKey) ?? 'GENERAL' })
    .eq('event_id', eventId)
    .in('event_line_id', ids)
    .select('event_line_id');
  if (error) return { movedIds: [], error };
  const movedIds = Array.isArray(data)
    ? data.map(row => String(row.event_line_id)).filter(Boolean)
    : [];
  return { movedIds, error: null };
}

/**
 * Checks if any event lines have custom reservation windows that do not inherit
 * from the event schedule. Imported lines can carry start_at/end_at even when
 * the visible fill/return fields are null, so both representations count.
 */
export async function checkEventLinesDateOverrides(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
}): Promise<{
  hasFillOverrides: boolean;
  hasReturnOverrides: boolean;
  fillOverrideCount: number;
  returnOverrideCount: number;
  error: PostgrestError | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId } = params;
  if (!eventId) {
    return { hasFillOverrides: false, hasReturnOverrides: false, fillOverrideCount: 0, returnOverrideCount: 0, error: null };
  }

  // Check for either visible or imported start-window overrides.
  const { data: fillData, error: fillError } = await supabase
    .from('event_lines')
    .select('event_line_id')
    .eq('event_id', eventId)
    .or('fill_date.not.is.null,start_at.not.is.null');

  if (fillError) {
    return { hasFillOverrides: false, hasReturnOverrides: false, fillOverrideCount: 0, returnOverrideCount: 0, error: fillError };
  }

  // Check for either visible or imported end-window overrides.
  const { data: returnData, error: returnError } = await supabase
    .from('event_lines')
    .select('event_line_id')
    .eq('event_id', eventId)
    .or('return_date.not.is.null,end_at.not.is.null');

  if (returnError) {
    return { hasFillOverrides: false, hasReturnOverrides: false, fillOverrideCount: 0, returnOverrideCount: 0, error: returnError };
  }

  const fillOverrideCount = Array.isArray(fillData) ? fillData.length : 0;
  const returnOverrideCount = Array.isArray(returnData) ? returnData.length : 0;

  return {
    hasFillOverrides: fillOverrideCount > 0,
    hasReturnOverrides: returnOverrideCount > 0,
    fillOverrideCount,
    returnOverrideCount,
    error: null,
  };
}

/**
 * Clears every line-level reservation-window override for the selected side.
 * This makes both native and imported lines inherit the event schedule.
 */
export async function clearEventLinesDateOverrides(params: {
  supabase?: SupabaseClient;
  eventId: string | null | undefined;
  clearFill?: boolean;
  clearReturn?: boolean;
}): Promise<{ clearedCount: number; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { eventId, clearFill = true, clearReturn = true } = params;
  if (!eventId) return { clearedCount: 0, error: null };

  const updates: Record<string, null> = {};
  if (clearFill) {
    updates.start_at = null;
    updates.fill_date = null;
    updates.fill_time = null;
  }
  if (clearReturn) {
    updates.end_at = null;
    updates.return_date = null;
    updates.return_time = null;
  }

  if (Object.keys(updates).length === 0) {
    return { clearedCount: 0, error: null };
  }

  const { data, error } = await supabase
    .from('event_lines')
    .update(updates)
    .eq('event_id', eventId)
    .select('event_line_id');

  if (error) return { clearedCount: 0, error };
  return { clearedCount: Array.isArray(data) ? data.length : 0, error: null };
}
